/**
 * Merit rules shared by the trigger (apps/worker), the merit routes (apps/api) and the worksheet
 * screens — remediation plan §5I, decisions Q2 and Q17. PURE: no I/O, no clock; every figure is a
 * decimal string in and a `Decimal` or decimal string out (CLAUDE.md invariant 2).
 *
 * ELIGIBILITY DEFAULTS (Q2). A cycle-level setting does not exist yet (there is no settings JSON on
 * `ReviewCycle`); these constants are the one place the defaults live until it does.
 *   - tenure cutoff: joined on or before `cycle.endsOn − 90 days`;
 *   - no compensation change in the 6 months before the matrix effective date (the employment's
 *     FIRST compensation row — the joining offer — is not a change);
 *   - a manager may set any increment, but beyond ±2 percentage points of the matrix figure the row
 *     needs a justification and is flagged to the approver.
 *
 * BUDGET (Q2). A recommendation's cost is `proposed − current` on its own pay basis, annualised so
 * rows on different bases add up: a `ctc` amount is already annual; `gross`/`net` are monthly ×12.
 */

import { Decimal } from 'decimal.js';
import type { PayBasis } from '../schemas/pay-basis.js';

/** ±percentage points from the matrix increment before a justification is required (Q2). */
export const MERIT_DEVIATION_TOLERANCE_PP = '2';
/** Joined on or before `cycle.endsOn` minus this many days (Q2, Q6). */
export const MERIT_TENURE_CUTOFF_DAYS = 90;
/** No compensation change within this many months before the effective date (Q2). */
export const MERIT_RECENT_CHANGE_MONTHS = 6;
/** An employment in one of these statuses on the effective date is "active" (the worker's list). */
export const MERIT_ACTIVE_EMPLOYMENT_STATUSES = [
  'probation',
  'confirmed',
  'on_leave',
  'notice_period',
] as const;

/** Whether the manager's percentage strays beyond the tolerance from the matrix percentage. */
export function isMeritDeviation(
  matrixIncrementPercent: string,
  proposedIncrementPercent: string,
  tolerancePp: string = MERIT_DEVIATION_TOLERANCE_PP,
): boolean {
  return new Decimal(proposedIncrementPercent)
    .minus(matrixIncrementPercent)
    .abs()
    .greaterThan(tolerancePp);
}

/** `current × (1 + percent / 100)`, 4 dp — the figure a recommendation and a proposal store. */
export function meritProposedAmount(currentAmount: string, incrementPercent: string): string {
  return new Decimal(currentAmount)
    .times(new Decimal(1).plus(new Decimal(incrementPercent).dividedBy(100)))
    .toFixed(4);
}

/** `proposed − current`, annualised by basis: ctc is annual already; gross and net are monthly. */
export function annualisedMeritDelta(
  payBasis: PayBasis,
  currentAmount: string,
  proposedAmount: string,
): Decimal {
  const delta = new Decimal(proposedAmount).minus(currentAmount);
  return payBasis === 'ctc' ? delta : delta.times(12);
}

/** Calendar arithmetic on `YYYY-MM-DD`, in UTC parts so no server timezone leaks in (invariant 3). */
function parts(date: string): { y: number; m: number; d: number } {
  const [y, m, d] = date.split('-').map(Number) as [number, number, number];
  return { y, m, d };
}

const iso = (y: number, m: number, d: number): string =>
  `${String(y).padStart(4, '0')}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`;

/** `date` plus `days` (negative to subtract). */
export function addCalendarDays(date: string, days: number): string {
  const { y, m, d } = parts(date);
  const t = new Date(Date.UTC(y, m - 1, d + days));
  return iso(t.getUTCFullYear(), t.getUTCMonth() + 1, t.getUTCDate());
}

/** `date` plus `months`, clamped to the target month's last day (31 Aug − 6 months = 28/29 Feb). */
export function addCalendarMonths(date: string, months: number): string {
  const { y, m, d } = parts(date);
  const index = y * 12 + (m - 1) + months;
  const ty = Math.floor(index / 12);
  const tm = (index % 12) + 1;
  const last = new Date(Date.UTC(ty, tm, 0)).getUTCDate();
  return iso(ty, tm, Math.min(d, last));
}

/** The latest joining date that still makes someone eligible (Q2 tenure cutoff). */
export function meritTenureCutoff(cycleEndsOn: string): string {
  return addCalendarDays(cycleEndsOn, -MERIT_TENURE_CUTOFF_DAYS);
}

/** A compensation row starting AFTER this date is a "recent change" for the effective date (Q2). */
export function meritRecentChangeCutoff(effectiveDate: string): string {
  return addCalendarMonths(effectiveDate, -MERIT_RECENT_CHANGE_MONTHS);
}
